import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { AvpCustomAdaptor } from './../../../base/shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { IncidentsAdministrationService } from './incidents-administration.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormHelper } from 'src/app/base/shared/form-helper';
import { CommandModel, GridModule, RowDataBoundEventArgs } from '@syncfusion/ej2-angular-grids';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { SfCommandButtonType } from 'src/app/base/models/syncfusion-data';
import { ServiceStatusClient, IncidentDTO, StatusServiceDTO, ServiceStatusLevel } from 'src/app/base/models/models.swagger.generated';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { IncidentEditFormType, IncidentMessageFormType } from './incident-edit-form/incident-edit-form';
import { IncidentEditFormComponent } from './incident-edit-form/incident-edit-form.component';
import { DropDownItem } from 'src/app/base/shared/drop-down-item.model';

import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-incidents-administration',
  templateUrl: './incidents-administration.component.html',
  styleUrls: ['./incidents-administration.component.scss'],
  imports: [SearchFieldComponent, CheckBoxModule, GridModule, ToolbarModule, IncidentEditFormComponent, TranslateModule],
  providers: [IncidentsAdministrationService]
})
export class IncidentsAdministrationComponent extends GridBaseComponent<IncidentEditFormType> implements OnInit, OnDestroy {
  public serviceDropdownItems: DropDownItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private incidentsAdministrationService: IncidentsAdministrationService,
    private serviceStatusClient: ServiceStatusClient,
    private fb: FormBuilder,
    translateService: TranslateService,
    private spinnerService: SpinnerService
  ) {
    super(null, translateService, incidentsAdministrationService);
    // Only delete command for incidents (no edit since incidents are immutable)
    const deleteCmd = GridConstantsFactory.createStandardGridCommands().find(c => c.type === SfCommandButtonType.Delete);
    this.gridCommands = [deleteCmd!];
    this.searchOptions = { fields: ['statusServiceName'] };

    // Disable double-click edit — incidents are immutable
    this.editSettings = {
      ...GridConstantsFactory.createStandardEditSettings(),
      allowEditing: false
    };
  }

  public ngOnInit() {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.incidentsAdministrationService, this.spinnerService),
    });
    this.isActiveColumnName = 'isDeleted';
    this.addDialogTitle = 'i18n.domain.incidents.overview.add';

    // Load services for the dropdown
    this.serviceStatusClient.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe(services => {
        this.serviceDropdownItems = (services ?? []).map(s => ({
          key: String(s.id),
          value: s.name
        } as DropDownItem));
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onRowDataBound(args: RowDataBoundEventArgs): void {
    if ((args.data as any)?.isDeleted) {
      const button = args.row!.getElementsByClassName('icon-trash-can');
      if (button && button.length > 0 && button[0].parentElement) {
        button[0].parentElement.hidden = true;
      }
    }
  }

  public formatStatus(data: any): string {
    switch (data?.status) {
      case ServiceStatusLevel.Operational:
        return this.translateService.instant('i18n.domain.status.levels.operational');
      case ServiceStatusLevel.Degraded:
        return this.translateService.instant('i18n.domain.status.levels.degraded');
      case ServiceStatusLevel.Down:
        return this.translateService.instant('i18n.domain.status.levels.down');
      default:
        return '';
    }
  }

  public formatBoolean(data: any, field: string): string {
    return data && data[field]
      ? this.translateService.instant('i18n.shared.yes')
      : this.translateService.instant('i18n.shared.no');
  }

  protected initializeEditForm(data: any): void {
    const messagesArray = this.fb.array<FormGroup<IncidentMessageFormType>>([]);

    // For existing incidents (view only), populate from existing messages
    if (data.id) {
      const messages = data.messages ?? [];
      for (const msg of messages) {
        messagesArray.push(this.fb.group<IncidentMessageFormType>({
          language: this.fb.control<string>(msg.language),
          message: this.fb.control<string>(msg.message),
        }));
      }
    }
    // For new incidents, messages will be populated dynamically when clients are selected

    this.gridEditForm = this.fb.group<IncidentEditFormType>({
      id: this.fb.control<number>(data.id),
      statusServiceId: this.fb.control<number>(data.statusServiceId, Validators.required),
      status: this.fb.control<number>(data.status ?? ServiceStatusLevel.Degraded, Validators.required),
      description: this.fb.control<string>(data.description ?? '', [Validators.required, Validators.maxLength(250)]),
      sendNotification: this.fb.control<boolean>(data.sendNotification ?? false),
      clientIds: this.fb.control<number[]>(data.clientIds ?? []),
      messages: messagesArray,
    });

    // If editing (viewing), disable the form since incidents are read-only
    if (data.id) {
      this.showSaveButton = false;
    } else {
      this.showSaveButton = true;
    }

    // When sendNotification is unchecked, clear messages and clientIds
    this.gridEditForm.controls.sendNotification.valueChanges
      .pipe(takeUntil(this.unsubscribeSubject))
      .subscribe(sendNotification => {
        if (!sendNotification) {
          this.gridEditForm.controls.messages.clear();
          this.gridEditForm.controls.clientIds.setValue([]);
        }
      });
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return { param: rowData.statusServiceName ?? '' };
  }

  protected override getDataToSave(): any {
    const formValue = this.gridEditForm.value;
    return {
      ...formValue,
      statusServiceId: Number(formValue.statusServiceId),
      status: Number(formValue.status),
      description: formValue.description ?? '',
      clientIds: formValue.clientIds ?? [],
    };
  }

  public onLanguagesChanged(languages: string[]): void {
    const messagesArray = this.gridEditForm.controls.messages;
    const normalizedLanguages = Array.from(new Set((languages ?? []).filter(language => !!language)));
    const currentLanguages = messagesArray.controls.map(control => control.controls.language.value);

    if (JSON.stringify(currentLanguages) === JSON.stringify(normalizedLanguages)) {
      return;
    }

    const existingMessages = new Map<string, string>();

    // Preserve existing message content
    for (const ctrl of messagesArray.controls) {
      const lang = ctrl.controls.language.value;
      const msg = ctrl.controls.message.value;
      if (lang && msg) {
        existingMessages.set(lang, msg);
      }
    }

    // Rebuild messages array with new languages (message is required when sendNotification is active)
    messagesArray.clear();
    for (const lang of normalizedLanguages) {
      messagesArray.push(this.fb.group<IncidentMessageFormType>({
        language: this.fb.control<string>(lang),
        message: this.fb.control<string>(existingMessages.get(lang) ?? '', Validators.required),
      }));
    }
    FormHelper.updateTreeValidity(messagesArray);
  }
}
